import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useAppTheme } from '../../contexts/ThemeContext';
import Button from '../Button';

const MAX_FILE_SIZE_MB = 10;
const MAX_FILES = 5;
const ACCEPTED_TYPES = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'image/svg+xml',
  'application/pdf',
  'text/plain',
  'text/csv',
  'text/html',
  'text/markdown',
  'application/json',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.ms-excel',
  'application/msword',
  'application/vnd.ms-powerpoint',
];

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const PaperclipIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
  </svg>
);

const getStyles = (theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0',
  },
  dropZone: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
  },
  dropOverlay: {
    position: 'absolute',
    inset: 0,
    backgroundColor: 'rgba(99, 102, 241, 0.12)',
    border: `2px dashed ${theme.colors.primary}`,
    borderRadius: theme.borderRadius?.md || '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
    pointerEvents: 'none',
  },
  dropOverlayText: {
    fontSize: theme.fontSize?.md || '14px',
    fontWeight: theme.fontWeight?.medium || 500,
    color: theme.colors.primary,
    backgroundColor: theme.colors.background?.sidebar || '#1e1e2e',
    padding: '6px 14px',
    borderRadius: theme.borderRadius?.sm || '4px',
  },
  textareaRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '8px',
  },
  textarea: {
    flex: 1,
    minHeight: '36px',
    maxHeight: '120px',
    padding: '8px 12px',
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.borderRadius?.md || '6px',
    backgroundColor: theme.colors.background?.main || '#121218',
    color: theme.colors.text?.primary || '#fff',
    fontSize: theme.fontSize?.md || '14px',
    resize: 'none',
    outline: 'none',
    fontFamily: 'inherit',
    lineHeight: '1.4',
  },
  sendButton: {
    padding: '8px 16px',
    backgroundColor: theme.colors.primary,
    color: theme.colors.text?.primary || '#fff',
    border: 'none',
    borderRadius: theme.borderRadius?.md || '6px',
    cursor: 'pointer',
    fontSize: theme.fontSize?.md || '14px',
    fontWeight: theme.fontWeight?.medium || 500,
    transition: 'background-color 0.2s ease, opacity 0.2s ease',
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  sendButtonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  fileChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    padding: '6px 0 0 0',
  },
  fileChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '3px 8px',
    backgroundColor: theme.colors.background?.sidebar || '#1e1e2e',
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.borderRadius?.sm || '4px',
    fontSize: theme.fontSize?.sm || '12px',
    color: theme.colors.text?.secondary || '#aaa',
    maxWidth: '220px',
  },
  fileChipName: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  fileChipSize: {
    color: theme.colors.text?.muted || '#666',
    flexShrink: 0,
  },
  fileChipRemove: {
    background: 'none',
    border: 'none',
    color: theme.colors.text?.muted || '#666',
    cursor: 'pointer',
    padding: '0 2px',
    fontSize: '14px',
    lineHeight: 1,
    flexShrink: 0,
    transition: 'color 0.15s ease',
  },
  fileChipRemoveHover: {
    color: theme.colors.danger || '#f44336',
  },
  fileError: {
    fontSize: theme.fontSize?.sm || '12px',
    color: theme.colors.danger || '#f44336',
    padding: '4px 0 0 0',
  },
});

const getTextareaScrollbarStyles = (theme) => `
  .chat-input-textarea::-webkit-scrollbar {
    width: 6px;
  }
  .chat-input-textarea::-webkit-scrollbar-track {
    background: ${theme.scrollbar?.trackSmall || 'transparent'};
  }
  .chat-input-textarea::-webkit-scrollbar-thumb {
    background: ${theme.scrollbar?.thumb || '#555'};
    border-radius: 3px;
  }
  .chat-input-textarea::-webkit-scrollbar-thumb:hover {
    background: ${theme.scrollbar?.thumbHover || '#777'};
  }
  .chat-input-textarea {
    scrollbar-width: thin;
    scrollbar-color: ${theme.scrollbar?.thumb || '#555'} ${theme.scrollbar?.trackSmall || 'transparent'};
  }
`;

const ChatInput = ({
  value,
  onChange,
  onSend,
  isLoading = false,
  disabled = false,
  placeholder = 'Type your message...',
  maxFiles = MAX_FILES,
  maxFileSizeMB = MAX_FILE_SIZE_MB,
  acceptedTypes = ACCEPTED_TYPES,
  showUpload = true,
  maxTextareaHeight = 120,
  textareaStyle: textareaStyleOverride,
  sendButtonStyle: sendButtonStyleOverride,
  sendButtonDisabledStyle: sendButtonDisabledStyleOverride,
  sendLabel = 'Send',
}) => {
  const { theme } = useAppTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);

  const [files, setFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [fileError, setFileError] = useState(null);

  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const dragCounter = useRef(0);

  // Inject themed scrollbar styles for the textarea
  useEffect(() => {
    const styleId = 'chat-input-textarea-scrollbar-styles';
    let style = document.getElementById(styleId);
    if (!style) {
      style = document.createElement('style');
      style.id = styleId;
      document.head.appendChild(style);
    }
    style.textContent = getTextareaScrollbarStyles(theme);
  }, [theme]);

  // Auto-resize textarea and toggle overflow
  useEffect(() => {
    const el = textareaRef.current;
    if (el) {
      el.style.overflowY = 'hidden';
      el.style.height = '36px';
      const scrollH = el.scrollHeight;
      const capped = Math.min(scrollH, maxTextareaHeight);
      el.style.height = capped + 'px';
      el.style.overflowY = scrollH > maxTextareaHeight ? 'auto' : 'hidden';
    }
  }, [value, maxTextareaHeight]);

  const validateFiles = useCallback(
    (incoming) => {
      const errors = [];
      const valid = [];

      for (const file of incoming) {
        if (acceptedTypes.length > 0 && !acceptedTypes.includes(file.type)) {
          errors.push(`"${file.name}" has unsupported type`);
          continue;
        }
        if (file.size > maxFileSizeMB * 1024 * 1024) {
          errors.push(`"${file.name}" exceeds ${maxFileSizeMB}MB limit`);
          continue;
        }
        valid.push(file);
      }

      return { valid, errors };
    },
    [acceptedTypes, maxFileSizeMB],
  );

  const addFiles = useCallback(
    (incoming) => {
      setFileError(null);
      const { valid, errors } = validateFiles(incoming);

      if (errors.length > 0) {
        setFileError(errors.join('. '));
      }

      setFiles((prev) => {
        const combined = [...prev, ...valid];
        if (combined.length > maxFiles) {
          setFileError((prev) => {
            const msg = `Maximum ${maxFiles} files allowed`;
            return prev ? `${prev}. ${msg}` : msg;
          });
          return combined.slice(0, maxFiles);
        }
        return combined;
      });
    },
    [validateFiles, maxFiles],
  );

  const removeFile = useCallback((index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setFileError(null);
  }, []);

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer?.items?.length > 0) {
      setIsDragOver(true);
    }
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragOver(false);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      dragCounter.current = 0;

      if (!showUpload || disabled || isLoading) return;

      const droppedFiles = Array.from(e.dataTransfer?.files || []);
      if (droppedFiles.length > 0) {
        addFiles(droppedFiles);
      }
    },
    [showUpload, disabled, isLoading, addFiles],
  );

  const handleFileInputChange = useCallback(
    (e) => {
      const selected = Array.from(e.target.files || []);
      if (selected.length > 0) {
        addFiles(selected);
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [addFiles],
  );

  const handleSend = useCallback(async () => {
    const text = (value || '').trim();
    if ((!text && files.length === 0) || isLoading || disabled) return;

    let attachments = [];
    if (files.length > 0) {
      attachments = await Promise.all(
        files.map(async (file) => ({
          name: file.name,
          type: file.type,
          size: file.size,
          data: await fileToBase64(file),
        })),
      );
    }

    setFiles([]);
    setFileError(null);

    if (onSend) {
      onSend(text, attachments);
    }
  }, [value, files, isLoading, disabled, onSend]);

  const handleKeyPress = useCallback(
    (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  const canSend = ((value || '').trim() || files.length > 0) && !isLoading && !disabled;

  const mergedTextareaStyle = { ...styles.textarea, ...(textareaStyleOverride || {}) };

  return (
    <div style={styles.container}>
      <div
        style={styles.dropZone}
        onDragEnter={showUpload ? handleDragEnter : undefined}
        onDragLeave={showUpload ? handleDragLeave : undefined}
        onDragOver={showUpload ? handleDragOver : undefined}
        onDrop={showUpload ? handleDrop : undefined}
      >
        {isDragOver && (
          <div style={styles.dropOverlay}>
            <span style={styles.dropOverlayText}>Drop files here</span>
          </div>
        )}

        <div style={styles.textareaRow}>
          <textarea
            ref={textareaRef}
            className="chat-input-textarea"
            style={mergedTextareaStyle}
            value={value}
            onChange={(e) => onChange && onChange(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={placeholder}
            disabled={isLoading || disabled}
            rows={1}
          />
          {showUpload && (
            <Button
              type="button"
              color="core"
              iconOnly
              size="md"
              onClick={() => fileInputRef.current?.click()}
              disabled={isLoading || disabled}
              title="Attach files"
            >
              <PaperclipIcon />
            </Button>
          )}
          <Button
            color="core"
            size="md"
            onClick={handleSend}
            disabled={!canSend}
            style={sendButtonStyleOverride || undefined}
          >
            {sendLabel}
          </Button>
        </div>
      </div>

      {files.length > 0 && (
        <div style={styles.fileChips}>
          {files.map((file, index) => (
            <div key={`${file.name}-${index}`} style={styles.fileChip}>
              <span style={styles.fileChipName} title={file.name}>
                {file.name}
              </span>
              <span style={styles.fileChipSize}>{formatFileSize(file.size)}</span>
              <button
                style={styles.fileChipRemove}
                onClick={() => removeFile(index)}
                onMouseEnter={(e) => {
                  e.target.style.color = theme.colors.danger || '#f44336';
                }}
                onMouseLeave={(e) => {
                  e.target.style.color = theme.colors.text?.muted || '#666';
                }}
                title="Remove file"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {fileError && <div style={styles.fileError}>{fileError}</div>}

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={acceptedTypes.join(',')}
        style={{ display: 'none' }}
        onChange={handleFileInputChange}
      />
    </div>
  );
};

export default ChatInput;
